import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatINR, formatDate } from "@/lib/sections";
import { toast, Toaster } from "sonner";
import { Plus, X, UserPlus, Lock, Trash2 } from "lucide-react";

// Compute the next N dates matching any of the given weekdays (0=Mon…6=Sun).
// Used by the walk-in form to show valid session dates for a per_session event.
const nextMatchingDates = (weekdays, n = 12) => {
  const out = [];
  const allowed = new Set((weekdays || []).map(Number).filter(v => v >= 0 && v <= 6));
  if (!allowed.size) return out;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const cursor = new Date(today);
  let safety = 0;
  while (out.length < n && safety < 365 * 3) {
    const ourWeekday = (cursor.getDay() + 6) % 7;
    if (allowed.has(ourWeekday)) out.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
    safety++;
  }
  return out;
};
const isoDate = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
const fmtDate = d => d.toLocaleDateString("en-IN", { weekday:"short", day:"numeric", month:"short", year:"numeric" });

export default function AdminBookings() {
  const [items, setItems] = useState([]);
  const [events, setEvents] = useState([]);
  const [pools, setPools] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showWalkin, setShowWalkin] = useState(false);
  const [showBlock, setShowBlock] = useState(false);
  const [walkin, setWalkin] = useState({ event_id: "", name: "", phone: "", email: "", quantity: 1, session_date: "", notes: "" });
  const [block, setBlock] = useState({ pool: "", date: "", time_slot: "", units: 1, note: "" });
  const [submitting, setSubmitting] = useState(false);

  const reload = () => {
    api.get("/admin/bookings").then(r => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/admin/resource-blocks").then(r => setBlocks(r.data.blocks || [])).catch(() => {});
  };
  useEffect(() => {
    reload();
    api.get("/admin/events").then(r => setEvents(r.data)).catch(() => {});
    api.get("/admin/resource-pools").then(r => setPools(r.data.pools || [])).catch(() => {});
  }, []);

  const filtered = filter === "all" ? items : filter === "walkin" ? items.filter(i => i.is_walkin) : items.filter(i => i.status === filter);

  const selectedEvent = events.find(e => e.id === walkin.event_id);
  const sessionOptions = selectedEvent && selectedEvent.booking_mode === "per_session"
    ? nextMatchingDates(selectedEvent.recurrence_weekdays || (selectedEvent.recurrence_weekday != null ? [selectedEvent.recurrence_weekday] : []), 12)
    : [];

  const submitWalkin = async (e) => {
    e.preventDefault();
    if (!walkin.event_id || !walkin.name.trim()) {
      toast.error("Please pick an event and enter a name");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/admin/bookings/walkin", {
        event_id: walkin.event_id,
        name: walkin.name.trim(),
        phone: walkin.phone.trim(),
        email: walkin.email.trim(),
        quantity: Math.max(1, Number(walkin.quantity) || 1),
        session_date: walkin.session_date || null,
        notes: walkin.notes.trim(),
      });
      toast.success("Walk-in recorded");
      setShowWalkin(false);
      setWalkin({ event_id: "", name: "", phone: "", email: "", quantity: 1, session_date: "", notes: "" });
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not save walk-in"));
    } finally { setSubmitting(false); }
  };

  const submitBlock = async (e) => {
    e.preventDefault();
    if (!block.pool || !block.date) {
      toast.error("Please pick a resource and a date.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/admin/resource-blocks", {
        pool: block.pool,
        date: block.date,
        time_slot: block.time_slot.trim(),
        units: Math.max(1, Number(block.units) || 1),
        note: block.note.trim(),
      });
      toast.success("Resource blocked");
      setShowBlock(false);
      setBlock({ pool: "", date: "", time_slot: "", units: 1, note: "" });
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not block resource"));
    } finally { setSubmitting(false); }
  };

  const removeBlock = async (b) => {
    if (!window.confirm(`Remove this block? (${b.pool} · ${b.date}${b.time_slot ? " · " + b.time_slot : " · all-day"} · ${b.units} unit${b.units>1?"s":""})`)) return;
    try {
      await api.delete(`/admin/resource-blocks/${b.id}`);
      toast.success("Block removed");
      reload();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-bookings">
      <Toaster position="top-center" richColors />

      <div className="flex items-baseline justify-between">
        <div>
          <div className="overline">Sales</div>
          <h1 className="display text-5xl mt-3">Bookings.</h1>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowBlock(true)} className="btn-ghost text-xs" data-testid="admin-block-open">
            <Lock size={12}/> Block resource
          </button>
          <button onClick={() => setShowWalkin(true)} className="btn-primary" data-testid="admin-walkin-open">
            <UserPlus size={14}/> Add walk-in
          </button>
        </div>
      </div>

      <div className="mt-8 flex gap-2">
        {["all", "paid", "pending", "failed", "walkin"].map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`text-xs px-3 py-2 border capitalize ${filter === s ? "bg-charcoal text-cream border-charcoal" : "border-soft hover:bg-cream-2"}`}
            style={filter === s ? { background: "#1C1C19", color: "#F9F8F6" } : {}}
            data-testid={`booking-filter-${s}`}
          >
            {s === "walkin" ? "Walk-ins" : s}
          </button>
        ))}
      </div>

      <div className="mt-8 border border-soft bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="text-left text-xs uppercase tracking-widest text-muted2">
            <tr>
              <th className="p-4">When</th>
              <th className="p-4">Event</th>
              <th className="p-4">Customer</th>
              <th className="p-4">Session</th>
              <th className="p-4">Qty</th>
              <th className="p-4">Amount</th>
              <th className="p-4">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="7" className="p-8 text-muted2">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan="7" className="p-8 text-muted2">No bookings.</td></tr>}
            {filtered.map(b => (
              <tr key={b.id} className="border-t border-soft" data-testid={`booking-row-${b.id}`}>
                <td className="p-4 text-xs">{formatDate(b.created_at)}</td>
                <td className="p-4">
                  <div className="font-serif text-base">{b.event_title}</div>
                  <div className="text-xs text-muted2 font-mono">{b.id.slice(0,8)}</div>
                </td>
                <td className="p-4">
                  <div>{b.name}</div>
                  {b.email && <div className="text-xs text-muted2">{b.email}</div>}
                  <div className="text-xs text-muted2">{b.phone}</div>
                </td>
                <td className="p-4 text-xs">
                  {b.session_dates && b.session_dates.length > 1
                    ? `${b.session_dates.length} sessions`
                    : (b.session_date || "—")}
                </td>
                <td className="p-4">{b.quantity}</td>
                <td className="p-4 font-serif">{b.amount_inr > 0 ? formatINR(b.amount_inr) : "—"}</td>
                <td className="p-4 text-xs">
                  {b.is_walkin && <span className="inline-block px-2 py-0.5 mr-1 text-[10px] tracking-wider uppercase" style={{background:"#D4A373",color:"#1C1C19"}}>Walk-in</span>}
                  {b.status === "paid"    && <span style={{color:"#4A5D4E"}}>Paid</span>}
                  {b.status === "pending" && <span className="text-muted2">Pending</span>}
                  {b.status === "failed"  && <span className="text-red-700">Failed</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Resource blocks (maintenance / private bookings not tied to any event) */}
      <div className="mt-10">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="overline">Held aside</div>
            <h2 className="font-serif text-2xl mt-1">Resource blocks</h2>
          </div>
          <div className="text-xs text-muted2">Blocks count toward capacity same as bookings — use for maintenance, private hires, personal reservations.</div>
        </div>
        {blocks.length === 0 ? (
          <div className="border border-soft p-6 text-sm text-muted2 bg-cream-2">No active blocks. Use <em>Block resource</em> above to hold aside a wheel for maintenance or a private client.</div>
        ) : (
          <div className="border border-soft bg-white overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead className="text-left text-xs uppercase tracking-widest text-muted2">
                <tr>
                  <th className="p-4">Resource</th>
                  <th className="p-4">Date</th>
                  <th className="p-4">Time</th>
                  <th className="p-4">Units</th>
                  <th className="p-4">Note</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {blocks.map(b => {
                  const pool = pools.find(p => p.name === b.pool);
                  return (
                    <tr key={b.id} className="border-t border-soft" data-testid={`block-row-${b.id}`}>
                      <td className="p-4">{pool?.label || b.pool}</td>
                      <td className="p-4">{b.date}</td>
                      <td className="p-4 text-xs text-muted2">{b.time_slot || "All day"}</td>
                      <td className="p-4">{b.units}</td>
                      <td className="p-4 text-xs">{b.note || "—"}</td>
                      <td className="p-4 text-right">
                        <button onClick={() => removeBlock(b)} className="text-muted2 hover:text-terra" title="Remove block" data-testid={`block-delete-${b.id}`}>
                          <Trash2 size={14}/>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Block resource modal */}
      {showBlock && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setShowBlock(false)}>
          <form
            onSubmit={submitBlock}
            onClick={e => e.stopPropagation()}
            className="bg-cream max-w-xl w-full p-8 border border-soft space-y-4"
            data-testid="block-form"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="overline">Hold aside</div>
                <h2 className="display text-3xl mt-2">Block resource</h2>
                <p className="text-xs text-muted2 mt-2">
                  Reserve units of a shared resource for maintenance, personal use or a private client. Not tied to any specific event — applies to every event that shares this pool on the date/time you pick.
                </p>
              </div>
              <button type="button" onClick={() => setShowBlock(false)} className="text-muted2 hover:text-charcoal"><X size={20}/></button>
            </div>

            <label className="block">
              <div className="overline mb-1">Resource pool</div>
              <select required value={block.pool} onChange={e => setBlock({...block, pool: e.target.value})} className="w-full border-b border-soft py-2 bg-transparent" data-testid="block-pool">
                <option value="">— pick a resource —</option>
                {pools.map(p => <option key={p.name} value={p.name}>{p.label} (cap {p.capacity})</option>)}
              </select>
            </label>

            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <div className="overline mb-1">Date</div>
                <input required type="date" value={block.date} onChange={e => setBlock({...block, date: e.target.value})} className="w-full border-b border-soft py-2 bg-transparent" data-testid="block-date"/>
              </label>
              <label className="block">
                <div className="overline mb-1">Time slot (optional)</div>
                <input type="time" value={block.time_slot} onChange={e => setBlock({...block, time_slot: e.target.value})} className="w-full border-b border-soft py-2 bg-transparent" data-testid="block-time"/>
                <div className="text-[10px] text-muted2 mt-1">Leave empty for an all-day block.</div>
              </label>
              <label className="block">
                <div className="overline mb-1">Units to block</div>
                <input type="number" min="1" value={block.units} onChange={e => setBlock({...block, units: e.target.value})} className="w-full border-b border-soft py-2 bg-transparent" data-testid="block-units"/>
              </label>
            </div>

            <label className="block">
              <div className="overline mb-1">Note (optional)</div>
              <input value={block.note} onChange={e => setBlock({...block, note: e.target.value})} className="w-full border-b border-soft py-2 bg-transparent" data-testid="block-note" placeholder="e.g. Kiln repair / Private booking - Jane"/>
            </label>

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setShowBlock(false)} className="btn-ghost text-xs">Cancel</button>
              <button type="submit" disabled={submitting} className="btn-primary text-xs" data-testid="block-submit">
                <Lock size={12}/> {submitting ? "Blocking…" : "Block resource"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Walk-in modal */}
      {showWalkin && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setShowWalkin(false)}>
          <form
            onSubmit={submitWalkin}
            onClick={e => e.stopPropagation()}
            className="bg-cream max-w-xl w-full p-8 border border-soft space-y-4"
            data-testid="walkin-form"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="overline">New</div>
                <h2 className="display text-3xl mt-2">Walk-in booking</h2>
                <p className="text-xs text-muted2 mt-2">No payment, no email — but the walk-in occupies wheel slots (or any capacity pool) same as online bookings.</p>
              </div>
              <button type="button" onClick={() => setShowWalkin(false)} className="text-muted2 hover:text-charcoal"><X size={20}/></button>
            </div>

            <label className="block">
              <div className="overline mb-1">Event</div>
              <select required value={walkin.event_id} onChange={e => setWalkin({...walkin, event_id: e.target.value, session_date: ""})} className="w-full border-b border-soft py-2 bg-transparent" data-testid="walkin-event">
                <option value="">— pick an event —</option>
                {events.map(ev => <option key={ev.id} value={ev.id}>{ev.title}</option>)}
              </select>
            </label>

            {selectedEvent && selectedEvent.booking_mode === "per_session" && sessionOptions.length > 0 && (
              <label className="block">
                <div className="overline mb-1">Session date</div>
                <select required value={walkin.session_date} onChange={e => setWalkin({...walkin, session_date: e.target.value})} className="w-full border-b border-soft py-2 bg-transparent" data-testid="walkin-session-date">
                  <option value="">— pick a date —</option>
                  {sessionOptions.map(d => <option key={isoDate(d)} value={isoDate(d)}>{fmtDate(d)}</option>)}
                </select>
              </label>
            )}

            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <div className="overline mb-1">Name</div>
                <input required value={walkin.name} onChange={e => setWalkin({...walkin, name: e.target.value})} className="w-full border-b border-soft py-2 bg-transparent" data-testid="walkin-name"/>
              </label>
              <label className="block">
                <div className="overline mb-1">Phone</div>
                <input value={walkin.phone} onChange={e => setWalkin({...walkin, phone: e.target.value})} className="w-full border-b border-soft py-2 bg-transparent" data-testid="walkin-phone"/>
              </label>
              <label className="block">
                <div className="overline mb-1">Email (optional)</div>
                <input type="email" value={walkin.email} onChange={e => setWalkin({...walkin, email: e.target.value})} className="w-full border-b border-soft py-2 bg-transparent" data-testid="walkin-email"/>
              </label>
              <label className="block">
                <div className="overline mb-1">Qty</div>
                <input type="number" min="1" value={walkin.quantity} onChange={e => setWalkin({...walkin, quantity: e.target.value})} className="w-full border-b border-soft py-2 bg-transparent" data-testid="walkin-qty"/>
              </label>
            </div>

            <label className="block">
              <div className="overline mb-1">Notes (optional)</div>
              <input value={walkin.notes} onChange={e => setWalkin({...walkin, notes: e.target.value})} className="w-full border-b border-soft py-2 bg-transparent" data-testid="walkin-notes" placeholder="e.g. paid ₹850 in cash"/>
            </label>

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setShowWalkin(false)} className="btn-ghost text-xs">Cancel</button>
              <button type="submit" disabled={submitting} className="btn-primary text-xs" data-testid="walkin-submit">
                <Plus size={12}/> {submitting ? "Saving…" : "Record walk-in"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
